import axios from 'axios';
import { extendContainerValidator } from '../utils';
import { ImageObjType } from '../types';

interface ContainerProps {
  containerIds?: string[];
  number?: string;
  short?: boolean;
  status?: string;
  type?: string;
  condition?: string;
  clientCode?: string;
  containerImageType?: string;
  hasLocation?: string;
  hideAwaitingPayment?: boolean;
  yardId?: string;
  clientId?: string;
  take?: number;
  skip?: number;
  notLoading?: boolean;
  pageParam?: number;
  isQuery?: boolean;
  allowHold?: boolean;
  clientYardOptional?: boolean;
}

class ContainersService {
  getAll = (props: any) => axios.get(`/containers`, props);

  options =
    ({ pageParam = 0, isQuery, allowHold, clientYardOptional, ...props }: ContainerProps) =>
    async () => {
      const { containerImageType = 'all', ...params }: any = {
        notLoading: true,
        ...props,
      };

      if ((params.clientId || params.yardId) && !clientYardOptional) {
        if (!params.clientId || !params.yardId)
          return isQuery ? [] : { data: [], totalCount: 0, pageParam };
      }

      if (params.number?.length < 3 || !extendContainerValidator(params.number))
        delete params.number;
      if (!params.type) delete params.type;
      if (!params.clientCode) delete params.clientCode;

      const res = await this.getAll({
        params,
      });
      let data = res.data.data.filter((container: any) => {
        if (containerImageType === 'all') return true;
        if (containerImageType === 'hasImages' && container.images.length > 0) return true;
        if (containerImageType === 'noImages' && container.images.length === 0) return true;

        return false;
      });

      data = data.map((container: any) => ({
        id: container.id,
        label: container.number,
        number: container.number,
        type: container.type,
        images: container.images,
        documents: container.documents,
        clientCode: container.clientYard?.client?.code,
        block: container.block || '',
        row: container.row != null && container.row !== '' ? String(container.row) : '',
        slot: container.slot != null && container.slot !== '' ? String(container.slot) : '',
        tier: container.tier != null && container.tier !== '' ? String(container.tier) : '',
        isContainerSale: !!container.sale,
        isHold: !!container.isHold,
        disabled: allowHold ? false : !!container.isHold,
        gateIn: { completedAt: container.gateIn?.completedAt },
      }));

      if (isQuery) return data;

      return { data, totalCount: res.data.totalCount, pageParam };
    };

  clientCodeOptions = (isSuperAdmin: boolean) => async () => {
    const props = { params: { notLoading: true } };
    const path = isSuperAdmin ? '/clients' : '/client-yards/clients';

    let res = await axios.get(path, props).then((res) => res.data.data);

    if (!isSuperAdmin) {
      res = res.filter((obj: any) => obj.status === 'ACTIVE');
    }

    return res.map((obj: any) => ({
      label: obj.code || obj.client.code,
      id: obj.id || obj.client.id,
    }));
  };

  uploadImages = (id: string, photos: ImageObjType[] = []) => {
    if (!photos.length) return true;

    const formData = new FormData();
    for (const i in photos.map((obj) => obj.file)) {
      formData.append(`images`, photos[i].file);
    }

    return axios.patch(`/containers/${id}/upload-images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };

  deleteImages = (id: string, photos: ImageObjType[] = []) => {
    if (!photos.length) return true;

    return axios.patch(`/containers/${id}/delete-images`, { names: photos.map((obj) => obj.name) });
  };

  updateImages = async ({ id, photos = [] }: { id: string; photos: ImageObjType[] }) => {
    await this.deleteImages(
      id,
      photos.filter((obj) => obj.action === 'delete'),
    );

    return this.uploadImages(
      id,
      photos.filter((obj) => obj.action === 'upload'),
    );
  };

  uploadDocuments = (id: string, documents: ImageObjType[] = []) => {
    if (!documents.length) return true;

    const formData = new FormData();
    for (const i in documents.map((obj) => obj.file)) {
      formData.append(`documents`, documents[i].file);
    }

    return axios.patch(`/containers/${id}/upload-documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };

  deleteDocuments = (id: string, documents: ImageObjType[] = []) => {
    if (!documents.length) return true;

    return axios.patch(`/containers/${id}/delete-documents`, {
      names: documents.map((obj) => obj.name),
    });
  };

  updateDocuments = async ({ id, documents = [] }: { id: string; documents?: ImageObjType[] }) => {
    await this.deleteDocuments(
      id,
      documents.filter((obj) => obj.action === 'delete'),
    );

    return this.uploadDocuments(
      id,
      documents.filter((obj) => obj.action === 'upload'),
    );
  };

  viewDocument = (href: any = '') => {
    if (!href) return;
    let url = href;

    if (typeof href === 'object') url = window.URL.createObjectURL(href);

    window.open(url, '_blank');

    if (typeof href === 'object') window.URL.revokeObjectURL(url);
  };

  edit = ({ id, ...props }: any) => axios.patch(`/containers/${id}`, props);

  removePlace = (id: string) => axios.patch(`/containers/${id}/remove-place`);
}

export const containersService = new ContainersService();
